import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Trash2 } from "lucide-react";
import { api, rp } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { logError } from "@/lib/log";

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";
const SOURCES = ["Instagram DM", "WhatsApp", "Offline", "Community", "Corporate", "Café Partner", "Other"];

export const HistoricalRentals = ({ customerId, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ rental_date: "", package_id: "", quantity: 1, final_price: 0,
    source: "Instagram DM", notes: "", count_toward_tier: true, include_in_spend: true });

  const load = () => api.get(`/customers/${customerId}/historical-rentals`)
    .then((r) => setRows(r.data)).catch((e) => logError("Historical load failed", e));
  useEffect(() => {
    if (!customerId) return;
    load();
    api.get("/packages").then((r) => setPkgs(r.data)).catch((e) => logError("Packages failed", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const save = async () => {
    try {
      const pkg = pkgs.find((p) => p.id === f.package_id);
      await api.post(`/customers/${customerId}/historical-rentals`, { ...f, package_name: pkg?.name });
      toast.success("Sewa lama dicatat");
      setF({ ...f, rental_date: "", final_price: 0, notes: "" });
      setOpen(false); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/historical-rentals/${id}`); load(); onChanged?.(); toast.success("Dihapus"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="historical-rentals">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-widest text-ink-soft flex items-center gap-1.5">
          <History size={13} /> Sewa Lama (Instagram / WhatsApp)
        </div>
        <button data-testid="add-historical-btn" onClick={() => setOpen(!open)}
          className="h-8 px-3 rounded-md border border-black/15 text-xs hover:border-lm-red">+ Catat sewa lama</button>
      </div>

      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3 border border-black/10 rounded-md p-3" data-testid="historical-form">
          <label className="block"><span className="text-[11px] text-ink-soft">Tanggal sewa</span>
            <Input className="mt-1" type="date" data-testid="hr-date" value={f.rental_date} onChange={(e) => setF({ ...f, rental_date: e.target.value })} />
          </label>
          <label className="block"><span className="text-[11px] text-ink-soft">Paket</span>
            <select className={`${sel} mt-1`} data-testid="hr-package" value={f.package_id} onChange={(e) => setF({ ...f, package_id: e.target.value })}>
              <option value="">— pilih paket —</option>
              {pkgs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-[11px] text-ink-soft">Jumlah unit</span>
            <Input className="mt-1" type="number" data-testid="hr-qty" value={f.quantity} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) })} />
          </label>
          <label className="block"><span className="text-[11px] text-ink-soft">Harga final (Rp)</span>
            <Input className="mt-1" type="number" data-testid="hr-price" value={f.final_price} onChange={(e) => setF({ ...f, final_price: Number(e.target.value) })} />
          </label>
          <label className="block"><span className="text-[11px] text-ink-soft">Sumber</span>
            <select className={`${sel} mt-1`} data-testid="hr-source" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}>
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-[11px] text-ink-soft">Catatan</span>
            <Input className="mt-1" data-testid="hr-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </label>
          <div className="sm:col-span-2 flex flex-wrap items-center gap-3 text-xs">
            <button data-testid="hr-count-tier" onClick={() => setF({ ...f, count_toward_tier: !f.count_toward_tier })}
              className={`px-3 py-1.5 rounded-full border ${f.count_toward_tier ? "bg-emerald-50 border-emerald-300" : "border-black/15"}`}>
              Hitung untuk tier &amp; loyalty: {f.count_toward_tier ? "Ya" : "Tidak"}
            </button>
            <button data-testid="hr-count-spend" onClick={() => setF({ ...f, include_in_spend: !f.include_in_spend })}
              className={`px-3 py-1.5 rounded-full border ${f.include_in_spend ? "bg-emerald-50 border-emerald-300" : "border-black/15"}`}>
              Masuk total belanja: {f.include_in_spend ? "Ya" : "Tidak"}
            </button>
            <button data-testid="hr-save" onClick={save} className="ml-auto h-9 px-4 rounded-md bg-ink text-cream text-xs hover:bg-lm-red">Simpan</button>
          </div>
        </div>
      )}

      <div className="mt-2 divide-y divide-black/5">
        {rows.map((b) => (
          <div key={b.id} className="py-1.5 flex items-center justify-between gap-3 text-xs" data-testid={`historical-${b.booking_code}`}>
            <span>{b.rental_date} · {b.package_name_snapshot} · {b.quantity} unit · {rp(b.final_rental_total)} · {b.source}</span>
            <span className="flex items-center gap-2">
              {b.exclude_from_tier && <span className="text-ink-soft">tidak dihitung tier</span>}
              <button data-testid={`del-historical-${b.booking_code}`} onClick={() => remove(b.id)} className="text-ink-soft hover:text-lm-red"><Trash2 size={13} /></button>
            </span>
          </div>
        ))}
        {rows.length === 0 && <div className="py-2 text-xs text-ink-soft">Belum ada catatan sewa lama.</div>}
      </div>
    </div>
  );
};
